import { Component, Host, h, Prop, Element, Event, EventEmitter, Watch } from '@stencil/core';
import { randomRGB } from '../../utils/random';

// Append a tiny dot to <aha-calendar> when a calendar-event is present.
// Done via an event vs as part of `connectedCallback` or other lifecycle event to ensure that the parent <aha-calendar>
// component has fully rendered.
window.addEventListener('aha-calendar-event:added', (event: any) => {
  const calendarEvent = event.target;
  const calendar = event.composedPath().find((node: any) => node.tagName === 'AHA-CALENDAR');
  if (calendar) {
    const dot = document.createElement('aha-calendar-event-dot');
    dot.slot = `event-dot-${calendarEvent.slot}`;
    dot.color = calendarEvent.color;
    calendar.append(dot);
    calendarEvent.dot = dot;
  }
});

export interface AhaCalendarEvent extends HTMLAhaCalendarEventElement {}

/**
 * Typically used as a child element of an `<aha-calendar>` component. In this usage, it should have a `slot` attribute set to a `YYYY-MM-DD` formatted date, which will automatically slot it into the proper position within the `<aha-calendar>` element.
 *
 * When used within this context, the `<aha-calendar-event>` will display a small dot on the calendar date which matches the event date, and will also automatically display a tooltip that shows the detail of the event when activated.
 *
 * Note that an `<aha-calendar>` component can each have multiple `<aha-calendar-event>` components with the same date. These will automatically be slotted into the correct location within the parent component and will display as separate events.
 *
 * When used _outside_ the context of an `<aha-calendar>` component the `<aha-calendar-event>` will just show a colored dot and some description text next to it.
 *
 * @slot default - Contents of the default slot.
 */
@Component({
  tag: 'aha-calendar-event',
  styleUrl: 'aha-calendar-event.css',
  shadow: true,
})
export class AhaCalendarEvent {
  @Element() host!: HTMLAhaCalendarEventElement;

  /** The color of the event "dot". Can be any valid CSS color value (`red`, `#f00`, `#f00000`, `rgba(255, 0, 0, 0.3)`, `var(--my-color)`). */
  @Prop({ reflect: true }) color: string = randomRGB(5);

  @Watch('color')
  private handleUpdatedColor() {
    this.updatedEvent.emit(this.host);
  }

  /**
   * Triggers when the event is added to the page.
   */
  @Event({
    eventName: 'aha-calendar-event:added',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  addedEvent!: EventEmitter;

  /**
   * Triggers when the event is updated.
   */
  @Event({
    eventName: 'aha-calendar-event:updated',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  updatedEvent!: EventEmitter;

  /**
   * Triggers when the event is removed from the page.
   */
  @Event({
    eventName: 'aha-calendar-event:removed',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  removedEvent!: EventEmitter;

  componentDidRender() {
    this.addedEvent.emit(this.host);
  }

  disconnectedCallback() {
    if (this.dot) this.dot.remove();
    this.removedEvent.emit(this.host);
  }

  private static handleSlotChange(event: any) {
    const host = event.target.getRootNode().host;
    host.updatedEvent.emit(host);
  }

  private dot!: HTMLAhaCalendarEventDotElement;

  render() {
    return (
      <Host>
        <aha-calendar-event-dot color={this.color}></aha-calendar-event-dot>
        <slot onSlotchange={AhaCalendarEvent.handleSlotChange}></slot>
      </Host>
    );
  }
}
